import log from 'loglevel';
import { getLimitField } from './commonUtils';
import { FilterDefinitionGraphQLQueryMapper } from '../mappers';
import { FilterDefinition } from '../models/FilterDefinition/FilterDefinition';
import { FilterRowSelection } from '../types/filter';
import { ExclusionRule } from '../types/ExclusionRule';

const filterDefinitionGraphQLQueryMapper =
  new FilterDefinitionGraphQLQueryMapper();

export function buildSearchListQuery(
  queryName: string,
  filterFields: readonly FilterDefinition[],
  filterSelections: Record<string, FilterRowSelection>,
  exclusions: ExclusionRule[] = [],
  nextToken?: string
): string {
  const filterClause = buildFilterClause(
    filterFields,
    filterSelections,
    false,
    exclusions
  );

  const limitField = getLimitField(filterFields);
  const sortClause = limitField?.databaseMetadata?.columnName
    ? getSortClause(limitField.databaseMetadata.columnName)
    : '';

  const nextTokenClause = nextToken ? `, ${getNextTokenClause(nextToken)}` : '';

  const fieldsClause = buildFieldsClause(filterFields);

  return `
    query ListQuery {
      ${queryName}(limit: 1000${filterClause}${nextTokenClause}${sortClause ? `, ${sortClause}` : ''}) {
        items {
          ${fieldsClause}
        }
        nextToken
        total
      }
    }
  `;
}

export function buildCountQuery(
  queryName: string,
  filterFields: FilterDefinition[],
  filterSelections: Record<string, FilterRowSelection>,
  exclusions: ExclusionRule[] = []
): string {
  const filterClause = buildFilterClause(
    filterFields,
    filterSelections,
    false,
    exclusions
  );

  const args = filterClause ? filterClause.replace(/^,\s*/, '') : '';

  return `
    query CountQuery {
      ${queryName}(${args}) {
        total
      }
    }
  `;
}

/**
 * Builds the GraphQL query for aggregate reports with grouping.
 */
export const buildSearchAggregateWithGroupingQuery = (
  openSearchIndex: string,
  filterFields: readonly FilterDefinition[],
  filterSelections: Record<string, FilterRowSelection>,
  exclusions: ExclusionRule[] = [],
  nextToken?: string
): string => {
  const filterClause = buildFilterClause(
    filterFields,
    filterSelections,
    true,
    exclusions
  );
  const nextTokenClause = nextToken ? `, ${getNextTokenClause(nextToken)}` : '';

  // Derive groupByText and groupByNumeric fields dynamically
  const groupByTextFields = filterFields
    .filter(f => f.isGroupBy && f.databaseMetadata?.dataType === 'string')
    .map(f => `"${f.databaseMetadata?.columnName}"`);

  const groupByNumericFields = filterFields
    .filter(f => f.isGroupBy && f.databaseMetadata?.dataType !== 'string')
    .map(f => `"${f.databaseMetadata?.columnName}"`);

  // Construct the groupByText and groupByNumeric clauses
  const groupByTextClause =
    groupByTextFields.length > 0
      ? `, groupByText: [${groupByTextFields.join(', ')}]`
      : '';

  const groupByNumericClause =
    groupByNumericFields.length > 0
      ? `, groupByNumeric: [${groupByNumericFields.join(', ')}]`
      : '';

  const query = `
    query GetAggregatedReport {
      getAggregatedResult(openSearchIndex: "${openSearchIndex}",limit: 1000${filterClause}${nextTokenClause}${groupByTextClause}${groupByNumericClause}) {
        items {
          key
          doc_count
        }
        nextToken
      }
    }
  `;

  log.debug('Generated Aggregated Grouping Query:', query);

  return query;
};

export function buildSearchAggregateQuery(
  dbColumn: string,
  queryName: string,
  filterFields: FilterDefinition[],
  filterSelections: Record<string, FilterRowSelection>,
  exclusions: ExclusionRule[] = [],
  nextToken?: string
): string {
  const filterClause = buildFilterClause(
    filterFields,
    filterSelections,
    false,
    exclusions
  );

  const nextTokenClause = nextToken ? `, ${getNextTokenClause(nextToken)}` : '';

  return `
    query AggregateQuery {
      ${queryName}(${filterClause}${nextTokenClause}, aggregates: { field: ${dbColumn}, type: terms, name: "${dbColumn}Terms" }) {
        aggregateItems {
          result {
            ... on SearchableAggregateBucketResult {
              __typename
              buckets {
                doc_count
                key
              }
            }
          }
          name
        }
        nextToken
        total
      }
    }
  `;
}

function buildFilterClause(
  filterFields: readonly FilterDefinition[],
  filterSelections: Record<string, FilterRowSelection>,
  passFilterAsString = false,
  exclusions: ExclusionRule[] = []
): string {
  // 1) Simplify *and use* these sanitized selections everywhere below
  const simplifiedSelections = getSimplifiedFilters(filterSelections);

  log.debug(
    'Building GraphQL query with filters (simplified):',
    simplifiedSelections
  );
  log.debug('Building GraphQL query with exclusions:', exclusions);

  const limitField = getLimitField(filterFields);

  const filterDefinitions = filterFields.filter(
    f => f.id !== limitField?.id // Exclude limit field from filter conditions
  );

  // 2) Map with simplified selections (so "ALL" never leaks)
  const filterConditions = filterDefinitionGraphQLQueryMapper.map({
    filterDefinitions,
    filterSelections: simplifiedSelections,
  });

  // Add exclusion conditions
  const exclusionConditions = buildExclusionConditions(
    exclusions,
    filterFields
  );
  const allConditions = [...filterConditions, ...exclusionConditions];

  if (allConditions.length === 0) {
    return '';
  }

  if (passFilterAsString) {
    const escapedFilterConditions = JSON.stringify(allConditions).replace(
      /"/g,
      '\\"'
    );
    return `, filter: "{\\"and\\":${escapedFilterConditions}}"`;
  } else {
    const filterObject = { and: allConditions };
    const filterString = JSON.stringify(filterObject).replace(
      /"([^"]+)":/g,
      '$1:'
    );
    return `, filter: ${filterString}`;
  }
}

/**
 * Simplifies the filters by removing:
 *  - selections with no values
 *  - the "ALL" sentinel (treat as “omit this field”)
 *  - externalUpdateHash (noise)
 * Also converts '' (Unspecified) to null so it matches your counts map keys.
 */
function getSimplifiedFilters(
  filters: Record<string, FilterRowSelection>
): Record<string, FilterRowSelection> {
  const out: Record<string, FilterRowSelection> = {};

  for (const [id, selection] of Object.entries(filters)) {
    // Strip "ALL" and normalize '' -> null
    const cleanedValues = (selection?.value ?? [])
      .filter(v => String(v) !== 'ALL')
      .map(v => (v === '' ? null : v));

    // If nothing left after stripping "ALL", omit this field entirely
    if (cleanedValues.length === 0) continue;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { externalUpdateHash, ...rest } = selection;

    out[id] = {
      ...rest,
      value: cleanedValues,
    };
  }

  return out;
}

/**
 * Builds the fields clause for the GraphQL query.
 *
 * @param filterFields - The filter fields.
 * @returns The fields clause string.
 */
function buildFieldsClause(filterFields: readonly FilterDefinition[]): string {
  return [
    ...new Set(filterFields.map(field => field.databaseMetadata?.columnName)),
  ].join('\n          ');
}

function getSortClause(sortField: string) {
  return `sort: { field: ${sortField}, direction: desc }`;
}

function getNextTokenClause(nextToken: string) {
  return `nextToken: "${nextToken}"`;
}

type ExclusionCondition = Record<string, { ne: string | number }>;

/**
 * Builds the exclusion conditions for a GraphQL query.
 * @param exclusions - The exclusion rules to apply.
 * @param filterFields - The filter fields.
 * @returns The exclusion conditions array.
 */
function buildExclusionConditions(
  exclusions: ExclusionRule[],
  filterFields: readonly FilterDefinition[]
): ExclusionCondition[] {
  const exclusionConditions: ExclusionCondition[] = [];

  exclusions.forEach(exclusion => {
    // Find the field that matches the exclusion's fieldName
    const field = filterFields.find(
      f => f.databaseMetadata?.columnName === exclusion.fieldName
    );

    if (!field) {
      log.warn(`Field not found for exclusion rule: ${exclusion.fieldName}`);
      return;
    }

    const columnName = field.databaseMetadata?.columnName;
    if (!columnName) {
      return;
    }

    // Handle different operators
    if (
      exclusion.operator === 'NOT_IN' &&
      exclusion.excludedValues.length > 0
    ) {
      // For NOT_IN operator, exclude all the specified values
      exclusion.excludedValues.forEach(value => {
        exclusionConditions.push({
          [columnName]: { ne: value },
        });
      });
    } else if (
      exclusion.operator === 'NE' &&
      exclusion.excludedValues.length > 0
    ) {
      // For NE operator, exclude the first value (assuming single value)
      exclusionConditions.push({
        [columnName]: { ne: exclusion.excludedValues[0] },
      });
    }
  });

  log.debug('Built exclusion conditions:', exclusionConditions);
  return exclusionConditions;
}
